import Dinero, { Currency } from "dinero.js";
import * as CurrencyCodes from "currency-codes";

export class CurrencyService {
    /**
     * Returns a formatted string representing a given amount with desired currency.
     * @param currency The currency to represent the amount in.
     * @param amount The amount in minor units to format.
     */
    formatCurrencyForDisplay(currency: Currency, amount: number) {
        const currencyFormatSettings = {
            amount: amount || 0,
            currency,
            precision: this.getPrecisionForCurrency(currency),
        };
        const formatter = Dinero(currencyFormatSettings);
        return formatter.toFormat(this.getSpecialFormat(currency));
    }

    /**
     * Get the number of decimal places expected in a currency string.
     * @param currency The currency to determine precision for.
     */
    getPrecisionForCurrency(currency: Currency) {
        const currencyCode = currency as string;
        const currencyCodeData = CurrencyCodes.code(currencyCode);
        if (!currencyCodeData) {
            return 2;
        }
        return currencyCodeData.digits;
    }

    /**
     * Sometimes default formatting will display format in a way that isn't natural
     * for natives of a currency. This function is intended to capture those specific cases and return a format
     * that makes the most sense.
     * @param currency The currency to test for special format.
     */
    private getSpecialFormat(currency: Currency) {
        const currencyCode = currency as string;
        const currencyCodeData = CurrencyCodes.code(currencyCode);

        // Special case for Chilean Peso (CLP) where the format is not standard.
        if (currencyCodeData?.code === "CLP") {
            return "USD0,0"; // USD will return the currency code given.
        }

        return undefined;
    }
}

const currencyService = new CurrencyService();

export class CurrencyContext {
    private readonly presentmentCurrency?: Currency = undefined;
    private readonly partnerCurrency: Currency;
    private readonly rateFrom: number;
    private readonly rateTo: number;

    constructor(rateFrom: number, rateTo: number, partnerCurrency: Currency, presentmentCurrency?: Currency) {
        this.presentmentCurrency = presentmentCurrency;
        this.partnerCurrency = partnerCurrency;
        this.rateFrom = rateFrom;
        this.rateTo = rateTo;
    }

    /**
     * @returns The currency code for the currency being used for monetary display.
     */
    code(): Currency {
        return this.presentmentCurrency ? this.presentmentCurrency : this.partnerCurrency;
    }

    /**
     * @returns true if the conversion is required, false otherwise.
     */
    conversionRequired(): boolean {
        return this.presentmentCurrency !== undefined && this.code() !== this.partnerCurrency;
    }

    /**
     * Converts a monetary value from a workflow in a partner specified currency
     * to the units of the currency being used for display.
     * @param amount The amount in minor units to convert.
     * @returns
     */
    getConvertedSubunits(amount: number): number {
        if (!this.conversionRequired()) {
            return amount;
        }
        return Math.ceil(
            this.subunits((this.units(amount, this.partnerCurrency) / this.rateFrom) * this.rateTo, this.code()),
        );
    }

    /**
     * Converts a monetary value from the database to the units of its currency.
     */
    private units(subUnits: number, currencyCode: Currency): number {
        return subUnits / Math.pow(10, currencyService.getPrecisionForCurrency(currencyCode));
    }

    /**
     * Converts a monetary value in units to the subunits of its currency.
     */
    private subunits(units: number, currencyCode: Currency): number {
        return units * Math.pow(10, currencyService.getPrecisionForCurrency(currencyCode));
    }
}

export { currencyService };
